import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { alertsAPI } from '../services/api';
import PropTypes from 'prop-types';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const previousCountRef = useRef(0);
  const audioRef = useRef(null);

  // Inicializar áudio de notificação
  useEffect(() => {
    // Criar elemento de áudio para notificações
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZRA0PVqjn77BZFw1Em+TwwWwgCDGBzvLaiTYIGGS75+ScRQwOUKji7bVgGgU+mtvy');
  }, []);

  // Tocar som de notificação
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(err => {
        console.log('Não foi possível tocar som de notificação:', err);
      });
    }
  }, []);

  // Mostrar notificação do navegador
  const showBrowserNotification = useCallback((notification) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notification.title, {
        body: notification.message,
        icon: '/icon-192.png',
        badge: '/icon-192.png',
        tag: notification.id,
        requireInteraction: false
      });
    }
  }, []);

  // Solicitar permissão para notificações do navegador
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  }, []);

  // Buscar notificações
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await alertsAPI.getMyAlerts();
      
      console.log('📥 Resposta da API:', response.data);
      
      // A API retorna { data: AlertRecipient[], meta: {...} }
      const alertRecipients = response.data?.data || response.data || [];
      
      console.log('📋 AlertRecipients recebidos:', alertRecipients.length);
      
      // Mapear AlertRecipient para o formato de notificação
      const mappedAlerts = alertRecipients.map(recipient => ({
        id: recipient.alert.id,
        title: recipient.alert.title,
        message: recipient.alert.message,
        priority: recipient.alert.priority,
        isRead: recipient.readAt !== null,
        createdAt: recipient.alert.createdAt,
        creator: recipient.alert.creator,
      }));
      
      console.log('📬 Notificações mapeadas:', mappedAlerts.length);
      
      // Verificar se há novas notificações não lidas
      const newUnreadCount = mappedAlerts.filter(alert => !alert.isRead).length;
      
      console.log('🔴 Não lidas:', newUnreadCount, '| Anterior:', previousCountRef.current);
      
      // Se houver novas notificações, tocar som e mostrar notificação
      if (newUnreadCount > previousCountRef.current && previousCountRef.current !== 0) {
        console.log('🆕 Nova notificação detectada!');
        playNotificationSound();
        
        // Mostrar notificação do navegador para a mais recente
        const latestUnread = mappedAlerts.find(alert => !alert.isRead);
        if (latestUnread) {
          showBrowserNotification(latestUnread);
        }
      }
      
      previousCountRef.current = newUnreadCount;
      setNotifications(mappedAlerts);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setLoading(false);
    }
  }, [playNotificationSound, showBrowserNotification]);

  // Marcar como lida
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await alertsAPI.markAsRead(notificationId);
      
      // Atualizar estado local
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, isRead: true } 
            : notif
        )
      );
      
      // Decrementar contador
      setUnreadCount(prev => Math.max(0, prev - 1));
      previousCountRef.current = Math.max(0, previousCountRef.current - 1);
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  }, []);

  // Marcar todas como lidas
  const markAllAsRead = useCallback(async () => {
    try {
      // Marcar todas as não lidas
      const unreadNotifications = notifications.filter(n => !n.isRead);
      
      await Promise.all(
        unreadNotifications.map(notif => alertsAPI.markAsRead(notif.id))
      );
      
      // Atualizar estado local
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, isRead: true }))
      );
      
      setUnreadCount(0);
      previousCountRef.current = 0;
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  }, [notifications]);

  // Deletar notificação
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await alertsAPI.delete(notificationId);
      
      // Remover do estado local
      setNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId);
        if (notification && !notification.isRead) {
          setUnreadCount(count => Math.max(0, count - 1));
          previousCountRef.current = Math.max(0, previousCountRef.current - 1);
        }
        return prev.filter(n => n.id !== notificationId);
      });
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
    }
  }, []);

  // Atualizar notificações periodicamente (polling a cada 30 segundos)
  useEffect(() => {
    fetchNotifications();
    requestNotificationPermission();

    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [fetchNotifications, requestNotificationPermission]);

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    requestNotificationPermission
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default NotificationContext;
